export enum BACKGROUND_OPERATION_STATUS {
  RUNNING = "RUNNING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
  CANCEL_REQUESTED = "CANCEL_REQUESTED",
  CANCELLED = "CANCELLED",
  UNKNOWN = "UNKNOWN",
}

export enum BACKGROUND_OPERATION_MODE {
  EPHEMERAL_JOB = "EPHEMERAL_JOB",
  IN_PROCESS = "IN_PROCESS",
}

export enum BACKGROUND_OPERATION_SUBJECT_TYPE {
  WORKFLOW = "WORKFLOW",
  DASHBOARD = "DASHBOARD",
  TRANSFORMATION = "TRANSFORMATION",
  STREAM = "STREAM",
  SCRIPT = "SCRIPT",
  LIST = "LIST",
  ACTIVITY = "ACTIVITY",
  MAINTENANCE = "MAINTENANCE",
  TEAM = "TEAM",
  CLIENT = "CLIENT",
}

export interface IBackgroundOperationLegacyMetadata {
  route_id?: string;
  action_right?: string;
  job_id?: string;
  workflow_id?: string;
  workflow_name?: string;
  workflow_report_id?: string;
  workflow_report_name?: string;
  workflow_report_status?: string;
  step_id?: string;
  step_name?: string;
  params?: Record<string, unknown>;
}

export interface IBackgroundOperationRequestContext {
  route_id?: string;
  action_right?: string;
  job_id?: string;
}

export interface IBackgroundOperationWorkflowContext {
  job_id?: string;
  workflow_id?: string;
  workflow_name?: string;
  workflow_report_id?: string;
  workflow_report_name?: string;
  report_status?: string;
  step_id?: string;
  step_name?: string;
}

export interface IBackgroundOperationContext {
  request?: IBackgroundOperationRequestContext;
  workflow?: IBackgroundOperationWorkflowContext;
  details?: Record<string, unknown>;
}

export interface IBackgroundOperation {
  id: string;
  client_id: string;
  operation_type: string;
  execution_mode: BACKGROUND_OPERATION_MODE;
  status: BACKGROUND_OPERATION_STATUS;
  created_at: string;
  action_code?: string;
  triggered_by_email?: string;
  k8s_job_name?: string;
  started_at?: string;
  ended_at?: string;
  duration_ms?: number;
  error_message?: string;
  subject_type?: BACKGROUND_OPERATION_SUBJECT_TYPE | string;
  subject_id?: string;
  subject_label?: string;
  context?: IBackgroundOperationContext;
  /** Legacy metadata field for backwards compatibility with earlier API responses. */
  metadata?: IBackgroundOperationLegacyMetadata;
}

/**
 * Payload attached by the backend to the RUN_IN_BACKGROUND business error.
 */
export interface IStartedBackgroundOperation {
  operation_id: string;
  operation_type: string;
}

const ONGOING_STATUSES = [
  BACKGROUND_OPERATION_STATUS.RUNNING,
  BACKGROUND_OPERATION_STATUS.CANCEL_REQUESTED,
];

export const isOperationOngoing = (operation: IBackgroundOperation): boolean =>
  ONGOING_STATUSES.includes(operation.status);

export const isOperationCancellable = (
  operation: IBackgroundOperation,
): boolean => operation.status === BACKGROUND_OPERATION_STATUS.RUNNING;

/**
 * Adapts a background operation received from the API to guarantee that
 * `subject_type`, `subject_label`, and `context` are consistently populated,
 * preserving compatibility when communicating with backends that return
 * legacy `metadata` payloads.
 */
export const normalizeBackgroundOperation = (
  operation: IBackgroundOperation,
): IBackgroundOperation => {
  const legacy = operation.metadata;
  if (!legacy && operation.context && operation.subject_type) {
    return operation;
  }

  const legacyWorkflowName = legacy?.workflow_name || legacy?.workflow_id;
  const isLegacyWorkflow = Boolean(legacyWorkflowName);

  const subject_type =
    operation.subject_type ??
    (isLegacyWorkflow ? BACKGROUND_OPERATION_SUBJECT_TYPE.WORKFLOW : undefined);
  const subject_id = operation.subject_id ?? legacy?.workflow_id;
  const subject_label =
    operation.subject_label ?? legacy?.workflow_name ?? legacy?.workflow_id;

  const existingWorkflowContext = operation.context?.workflow;
  const workflowContext: IBackgroundOperationWorkflowContext | undefined =
    existingWorkflowContext ??
    (isLegacyWorkflow ||
    legacy?.workflow_report_status ||
    legacy?.step_name ||
    legacy?.step_id
      ? {
          job_id: legacy?.job_id,
          workflow_id: legacy?.workflow_id,
          workflow_name: legacy?.workflow_name,
          workflow_report_id: legacy?.workflow_report_id,
          workflow_report_name: legacy?.workflow_report_name,
          report_status: legacy?.workflow_report_status,
          step_id: legacy?.step_id,
          step_name: legacy?.step_name,
        }
      : undefined);

  const existingRequestContext = operation.context?.request;
  const requestContext: IBackgroundOperationRequestContext | undefined =
    existingRequestContext ??
    (legacy?.route_id || legacy?.action_right || legacy?.job_id
      ? {
          route_id: legacy?.route_id,
          action_right: legacy?.action_right,
          job_id: legacy?.job_id,
        }
      : undefined);

  const context: IBackgroundOperationContext | undefined =
    operation.context || workflowContext || requestContext
      ? {
          ...operation.context,
          ...(requestContext ? { request: requestContext } : {}),
          ...(workflowContext ? { workflow: workflowContext } : {}),
        }
      : undefined;

  return {
    ...operation,
    subject_type,
    subject_id,
    subject_label,
    context,
  };
};
